import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronDown, ChevronUp, Mail } from "lucide-react";

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border-b border-gray-100">
    <button onClick={onClick} className="w-full py-4 flex items-start justify-between gap-4 text-left">
      <span className="font-medium text-gray-900 text-sm">{question}</span>
      {isOpen ? <ChevronUp size={18} className="text-[#3FA9F5] flex-shrink-0" /> : <ChevronDown size={18} className="text-gray-400 flex-shrink-0" />}
    </button>
    {isOpen && <div className="pb-4 text-gray-600 text-sm">{answer}</div>}
  </div>
);

const FleetyGoFAQsPage = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    { question: "What is FleetyGo?", answer: "FleetyGo is a hyperlocal delivery service that allows you to send packages up to 20 kg anywhere in the city via bike couriers." },
    { question: "What can I send through FleetyGo?", answer: "You can send documents, food, medicine, books, keys, clothes, electronics, and gifts. Alcohol, illegal items, weapons, and flammable materials are prohibited." },
    { question: "How much does it cost?", answer: "Delivery costs ₹10 per km with a minimum charge of ₹30 (for 0-3 km). There's also a ₹20 platform fee and ₹3.60 GST (18% of platform fee)." },
    { question: "What are the wait time charges?", answer: "The first 10 minutes of wait time at pickup and delivery locations is FREE. After 10 minutes, you'll be charged ₹1 per minute, which is paid directly to the rider." },
    { question: "How is the distance calculated?", answer: "Distance is calculated based on the actual road route between pickup and drop locations using Google Maps." },
    { question: "What payment methods are accepted?", answer: "We accept UPI (Google Pay, PhonePe, Paytm), Credit/Debit Cards, and Net Banking. Cash on Delivery is not available." },
    { 
      question: "How does the PIN verification work?", 
      answer: (
        <div>
          <p className="mb-2"><strong>Pickup PIN:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-2 mb-3">
            <li>You'll receive a 4-digit pickup PIN after payment</li>
            <li>Share this PIN with the rider when they arrive at the pickup location</li>
            <li>The rider cannot start the trip without verifying this PIN</li>
            <li>This ensures your package is picked up by the assigned rider</li>
          </ul>
          <p className="mb-2"><strong>Delivery PIN:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>The Delivery PIN is <strong>hidden until the rider reaches the delivery location</strong></li>
            <li>Once the rider marks arrival, the PIN is revealed in your app</li>
            <li>Share this PIN with the rider to confirm delivery</li>
            <li>This ensures the package is delivered only at the correct location</li>
          </ul>
        </div>
      )
    },
    { question: "Can I track my delivery?", answer: "Yes! You can track your rider's location in real-time on the map after payment is complete." },
    { question: "What if no rider is available?", answer: "If no rider is available within 10 minutes, your payment will be automatically refunded within 5-7 business days." },
    { 
      question: "Can I cancel my delivery?", 
      answer: (
        <div>
          <ul className="list-disc list-inside space-y-2">
            <li><strong>Before rider accepts the ride:</strong> Full refund, no cancellation fee</li>
            <li><strong>After rider accepts/starts for pickup:</strong> ₹30 cancellation fee applies (paid to the rider for their time). Rest of the amount will be refunded.</li>
            <li><strong>After pickup (trip started):</strong> Cancellation is not possible</li>
          </ul>
          <p className="mt-3 text-gray-600 text-sm">💡 You can cancel from the order tracking page by clicking "Cancel Order & Get Refund" button.</p>
        </div>
      )
    },
    { question: "What's the maximum weight limit?", answer: "The maximum weight limit is 20 kg. Items are categorized as Light (<5 kg), Medium (5-10 kg), and Heavy (10-20 kg). The rider may refuse packages exceeding 20 kg." },
    { question: "Is my package insured?", answer: "FleetyGo provides delivery services only and does not insure or guarantee the value or condition of items unless optional protection is purchased. Standard coverage is limited to ₹500 per package." },
    { 
      question: "How is my data protected?", 
      answer: (
        <div>
          <ul className="list-disc list-inside space-y-1">
            <li>Your data is encrypted and stored securely</li>
            <li>We use industry-standard security measures (SSL/HTTPS)</li>
            <li>Location data is only used during active deliveries</li>
            <li>Payment information is processed securely via Cashfree</li>
            <li>We never share your data with unauthorized third parties</li>
          </ul>
          <p className="mt-2">Read our full <a href="/fleetygo/privacy" className="text-[#3FA9F5] font-medium">Privacy Policy</a> for more details.</p>
        </div>
      )
    },
    { 
      question: "How can I delete my account?", 
      answer: (
        <div>
          <p className="mb-2">To delete your FleetyGo account:</p>
          <ol className="list-decimal list-inside space-y-1 ml-2">
            <li>Open the Fleety app and go to "My Fleety" menu</li>
            <li>Tap on "Delete my account" option (below Logout)</li>
            <li>Confirm your request in the warning dialog</li>
            <li>Your deletion request will be processed within 5-7 business days</li>
          </ol>
          <p className="mt-2 text-orange-600 text-sm">⚠️ Account deletion is permanent and cannot be undone. All your data including delivery history and saved addresses will be permanently deleted.</p>
        </div>
      )
    },
    { question: "How do I contact support?", answer: "You can reach us at support@fleety.co.in or call +91-98453 31817 for any assistance." }
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="fleetygo-faqs">
      <header className="bg-[#3FA9F5] p-4 flex items-center gap-4 sticky top-0 z-10">
        <button onClick={() => navigate(-1)} className="text-white">
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-white text-lg font-semibold">FAQs</h1>
      </header>

      <main className="flex-1 px-4 py-6 overflow-y-auto">
        <h1 className="text-xl font-bold text-gray-900 mb-1">FleetyGo FAQs</h1>
        <p className="text-sm text-gray-500 mb-6">Frequently Asked Questions</p>

        <div className="divide-y divide-gray-100">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
            />
          ))}
        </div>

        <div className="mt-8 p-4 bg-[#3FA9F5]/5 rounded-xl">
          <h3 className="font-semibold text-gray-900 mb-2">Still have questions?</h3>
          <p className="text-sm text-gray-600 mb-3">Contact our support team for assistance.</p>
          <a href="mailto:support@fleety.co.in" className="inline-flex items-center gap-2 text-[#3FA9F5] font-medium">
            <Mail size={18} />
            support@fleety.co.in
          </a>
        </div>
      </main>
    </div>
  );
};

export default FleetyGoFAQsPage;
